import { useState, useCallback, useEffect } from 'react';
import { 
  useAccount, 
  useConnect, 
  useDisconnect, 
  useWalletClient,
  usePublicClient,
  useWaitForTransactionReceipt,
  useSignTypedData,
  useChainId,
  useChains,
  useSwitchChain,
  useBalance,
} from 'wagmi';
import { parseEther, formatEther, encodeFunctionData, parseAbi } from 'viem';
import { connectors } from '../Web3Module/config';
import tokenAddresses from './tokenAddresses.json';

/**
 * 지갑 관련 기능을 제공하는 커스텀 훅입니다.
 * 지갑 연결, 트랜잭션 서명 및 전송, EIP-712 서명 요청, 연결된 지갑 정보 조회 기능을 제공합니다.
 * 
 * @returns {Object} 지갑 관련 기능과 상태
 */
export function useWallet() {
  // 상태 관리
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState(null);
  const [txHash, setTxHash] = useState(null);

  // wagmi 훅 사용
  const { address, isConnected, connector } = useAccount();
  const { connect, isLoading: isConnectLoading } = useConnect();
  const { disconnect } = useDisconnect();
  const { data: walletClient } = useWalletClient();
  const publicClient = usePublicClient();
  const { signTypedDataAsync } = useSignTypedData();
  const chains = useChains();
  const chainId = useChainId();
  const { switchChain } = useSwitchChain();
  const { data: balanceData } = useBalance({
    address,
    enabled: isConnected,
  });

  // 트랜잭션 영수증 대기 훅
  const { data: receipt, isLoading: isWaitingForReceipt } = useWaitForTransactionReceipt({
    hash: txHash,
    enabled: !!txHash,
  });

  const balance = balanceData ? balanceData.formatted : '0';

  // 에러 초기화
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  /**
   * 지갑 연결을 시도합니다.
   * @param {string} connectorId - 연결할 지갑 커넥터 ID
   * @returns {Promise<void>}
   */
  const connectWallet = useCallback(async (connectorId) => {
    try {
      setIsConnecting(true);
      setError(null);
      
      const connector = connectors.find(c => c.id === connectorId);
      if (!connector) {
        throw new Error(`지갑 커넥터를 찾을 수 없습니다: ${connectorId}`);
      }
      
      await connect({ connector });
    } catch (err) {
      console.error('지갑 연결 오류:', err);
      setError(err.message || '지갑 연결에 실패했습니다.');
    } finally {
      setIsConnecting(false);
    }
  }, [connect]);

  /**
   * 지갑 연결을 해제합니다.
   * @returns {Promise<void>}
   */
  const disconnectWallet = useCallback(async () => {
    try {
      setIsDisconnecting(true);
      setError(null);
      await disconnect();
    } catch (err) {
      console.error('지갑 연결 해제 오류:', err);
      setError(err.message || '지갑 연결 해제에 실패했습니다.');
    } finally {
      setIsDisconnecting(false);
    }
  }, [disconnect]);

  /**
   * 트랜잭션을 전송합니다.
   * @param {Object} txParams - 트랜잭션 파라미터
   * @param {string} txParams.to - 수신자 주소
   * @param {string} txParams.data - 트랜잭션 데이터
   * @param {string} [txParams.value] - 전송할 이더 값 (wei 단위)
   * @returns {Promise<string>} 트랜잭션 해시
   */
  const sendTransaction = useCallback(async (txParams) => {
    if (!walletClient) {
      throw new Error('지갑 클라이언트를 찾을 수 없습니다.');
    }

    try {
      setIsSending(true);
      setError(null);
      setTxHash(null);

      // 트랜잭션 전송
      const hash = await walletClient.sendTransaction({
        to: txParams.to,
        data: txParams.data,
        value: txParams.value ? BigInt(txParams.value) : 0n,
      });

      setTxHash(hash);
      return hash;
    } catch (err) {
      console.error('트랜잭션 전송 오류:', err);
      setError(err.message || '트랜잭션 전송에 실패했습니다.');
      throw err;
    } finally {
      setIsSending(false);
    }
  }, [walletClient]);

  /**
   * EIP-712 서명을 요청합니다.
   * @param {Object} domain - EIP-712 도메인 데이터
   * @param {Object} types - EIP-712 타입 정의
   * @param {Object} value - 서명할 데이터
   * @returns {Promise<string>} 서명 결과
   */
  const signTypedData = useCallback(async (domain, types, value) => {
    try {
      setError(null);
      
      if (!isConnected || !address) {
        throw new Error('지갑이 연결되어 있지 않습니다.');
      }
      
      // EIP-712 서명 요청
      const signature = await signTypedDataAsync({
        domain,
        types,
        primaryType: Object.keys(types).find(key => key !== 'EIP712Domain'),
        message: value,
      });
      
      return signature;
    } catch (err) {
      console.error('서명 요청 오류:', err);
      setError(err.message || '서명 요청에 실패했습니다.');
      throw err;
    }
  }, [signTypedDataAsync, isConnected, address]);

  /**
   * 개인 메시지 서명을 요청합니다.
   * @param {string} message - 서명할 메시지
   * @returns {Promise<string>} 서명 결과
   */
  const personalSign = useCallback(async (message) => {
    try {
      setError(null);
      
      if (!isConnected || !address) {
        throw new Error('지갑이 연결되어 있지 않습니다.');
      }
      
      // 개인 메시지 서명 요청
      const signature = await window.ethereum.request({
        method: 'personal_sign',
        params: [message, address],
      });
      
      return signature;
    } catch (err) {
      console.error('개인 메시지 서명 오류:', err);
      setError(err.message || '개인 메시지 서명에 실패했습니다.');
      throw err;
    }
  }, [isConnected, address]);

  /**
   * 연결된 지갑의 잔액을 조회합니다.
   * @returns {Promise<string>} 잔액 (ETH 단위)
   */
  const getBalance = useCallback(async () => {
    if (!address || !publicClient) {
      throw new Error('지갑이 연결되지 않았거나 클라이언트를 찾을 수 없습니다.');
    }

    try {
      const balance = await publicClient.getBalance({ address });
      return formatEther(balance);
    } catch (err) {
      console.error('잔액 조회 오류:', err);
      setError(err.message || '잔액 조회에 실패했습니다.');
      throw err;
    }
  }, [address, publicClient]);

  /**
   * 네트워크를 전환합니다.
   * @param {number} chainId - 전환할 체인 ID
   * @returns {Promise<void>}
   */
  const switchNetworkChain = useCallback(async (chainId) => {
    if (!switchChain) {
      throw new Error('네트워크 전환 기능을 사용할 수 없습니다.');
    }

    try {
      setError(null);
      await switchChain({ chainId });
    } catch (err) {
      console.error('네트워크 전환 오류:', err);
      setError(err.message || '네트워크 전환에 실패했습니다.');
      throw err;
    }
  }, [switchChain]);

  // 현재 체인 정보 가져오기
  const currentChain = chains.find(c => c.id === chainId);

  /**
   * 지갑에 call 요청을 보냅니다.
   * @param {Object} params - call 요청 파라미터
   * @param {string} params.to - 컨트랙트 주소
   * @param {string} params.data - 호출할 함수 데이터
   * @param {string} [params.blockTag='latest'] - 블록 태그 (기본값: 'latest')
   * @returns {Promise<string>} call 결과
   */
  const callContract = useCallback(async (params) => {
    if (!isConnected) {
      throw new Error('지갑이 연결되어 있지 않습니다.');
    }

    try {
      setError(null);
      
      // call 요청 보내기
      const result = await window.ethereum.request({
        method: 'eth_call',
        params: [
          {
            to: params.to,
            data: params.data,
          },
          params.blockTag || 'latest',
        ],
      });
      
      return result;
    } catch (err) {
      console.error('컨트랙트 호출 오류:', err);
      setError(err.message || '컨트랙트 호출에 실패했습니다.');
      throw err;
    }
  }, [isConnected]);

  return {
    // 상태
    address,
    isConnected,
    chainId,
    chain: currentChain,
    chains,
    isConnecting,
    isDisconnecting,
    isSending,
    isWaitingForReceipt,
    error,
    txHash,
    receipt,
    balance,
    
    // 사용 가능한 커넥터 목록
    connectors,
    
    // 메서드
    connectWallet,
    disconnectWallet,
    sendTransaction,
    signTypedData,
    personalSign,
    getBalance,
    switchNetworkChain,
    callContract,
  };
}

export default useWallet; 